import { CheckCircle2, Circle, Clock } from "lucide-react";

/**
 * Vertical progress timeline rendered inside the admin roster drawer.
 * Reads a milestones array shaped exactly like the /timeline endpoint response.
 * Achieved milestones render in oxblood with a filled dot + timestamp; pending
 * ones ghost out in cream/brown so admins can see the road ahead at a glance.
 */
export default function PhotographerTimeline({ milestones = [], loading = false }) {
  if (loading) {
    return (
      <div className="card-surface rounded-sm p-5" data-testid="photographer-timeline-loading">
        <div className="font-mono-tag text-[10px] text-[#78584B]">TIMELINE</div>
        <div className="mt-3 text-xs text-[#78584B]">Loading milestones…</div>
      </div>
    );
  }

  if (!milestones.length) return null;

  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  return (
    <div
      className="card-surface rounded-sm p-5"
      data-testid="photographer-timeline"
    >
      <div className="font-mono-tag text-[10px] text-[#78584B] mb-4">
        PROGRESS TIMELINE
      </div>

      <ol className="relative" data-testid="timeline-list">
        {milestones.map((m, idx) => {
          const isLast = idx === milestones.length - 1;
          const done = m.achieved && m.at;
          const inFlight = m.achieved && !m.at; // achieved status without a timestamp
          const pending = !m.achieved;
          const ts = fmt(m.at);

          return (
            <li
              key={m.key}
              data-testid={`timeline-item-${m.key}`}
              data-achieved={done ? "true" : "false"}
              className="relative pl-8 pb-5 last:pb-0"
            >
              {/* Connector line — hidden for the last node */}
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={`absolute left-[10px] top-4 bottom-0 w-px ${
                    done ? "bg-[#6B0F1A]/45" : "bg-[#1A0F0A]/12"
                  }`}
                />
              )}

              {/* Dot marker */}
              <span
                aria-hidden="true"
                className={`absolute left-0 top-0.5 w-[22px] h-[22px] rounded-full flex items-center justify-center border ${
                  done
                    ? "bg-[#6B0F1A] border-[#6B0F1A] text-[#F5EBD9]"
                    : inFlight
                    ? "bg-[#B08D57]/25 border-[#B08D57] text-[#B08D57]"
                    : "bg-[#F5EBD9] border-[#1A0F0A]/20 text-[#78584B]"
                }`}
              >
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.25} />
                ) : inFlight ? (
                  <Clock className="w-3 h-3" strokeWidth={2} />
                ) : (
                  <Circle className="w-2.5 h-2.5" strokeWidth={2} />
                )}
              </span>

              <div className="flex items-baseline gap-2 flex-wrap">
                <div
                  className={`font-display text-[15px] font-bold ${
                    pending ? "text-[#78584B]" : "text-[#1A0F0A]"
                  }`}
                >
                  {m.label}
                </div>
                {ts && (
                  <span
                    className="font-mono-tag text-[9px] text-[#78584B]"
                    data-testid={`timeline-timestamp-${m.key}`}
                  >
                    · {ts}
                  </span>
                )}
                {inFlight && (
                  <span className="font-mono-tag text-[9px] text-[#B08D57]">
                    · IN PROGRESS
                  </span>
                )}
                {pending && (
                  <span className="font-mono-tag text-[9px] text-[#78584B]/70">
                    · PENDING
                  </span>
                )}
              </div>
              {m.description && (
                <div
                  className={`text-[11px] leading-relaxed mt-0.5 ${
                    pending ? "text-[#78584B]/80" : "text-[#4D3F35]"
                  }`}
                >
                  {m.description}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
